import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import toast from "react-hot-toast";
import { sendFeedback } from "@/services/multi-agent";
import { FEEDBACK_RATING } from "@/constants/multi-agent/feedback.constants";
import { IconButton } from "@/components/atoms/multi-agent/Button";
import { BadResponsePopover } from "./BadResponsePopover";
import { useBadResponseFeedback } from "@/hooks/multi-agent/useBadResponseFeedback";

interface MessageFeedbackProps {
  /** The run ID for this message. Feedback is scoped to this run. */
  runId: string | null;
}

/**
 * MessageFeedback — "Was this helpful?" bar that mounts at the end of every
 * terminal Weave answer (current run and past turns).
 *
 * ThumbsUp  → fires a positive rating immediately (no dialog).
 * ThumbsDown → opens BadResponsePopover with reason selection + comment.
 *
 * After either action the buttons hide and a "Thanks" label appears,
 * preventing double-submission without any global state.
 */
export function MessageFeedback({ runId }: MessageFeedbackProps) {
  const [thumbsUpBusy, setThumbsUpBusy] = useState(false);
  const [thumbsUpDone, setThumbsUpDone] = useState(false);
  const badResponse = useBadResponseFeedback(runId);

  const handleThumbsUp = async () => {
    if (!runId || thumbsUpBusy || thumbsUpDone || badResponse.submitted) return;
    setThumbsUpBusy(true);
    try {
      await sendFeedback(runId, FEEDBACK_RATING.POSITIVE, null, []);
      setThumbsUpDone(true);
    } catch {
      toast.error("Couldn't send feedback. Please try again.");
    } finally {
      setThumbsUpBusy(false);
    }
  };

  const done = thumbsUpDone || badResponse.submitted;

  return (
    <div className="ma-fade-up mt-8 flex items-center gap-3">
      <span className="ma-micro" style={{ color: "var(--ma-text-muted)" }}>
        {done ? "Thanks for your feedback" : "Was this helpful?"}
      </span>

      {!done && (
        <>
          <IconButton
            type="button"
            onClick={handleThumbsUp}
            disabled={thumbsUpBusy}
            title="Helpful"
            aria-label="Helpful"
          >
            <ThumbsUp className="h-4 w-4" />
          </IconButton>

          <BadResponsePopover feedback={badResponse}>
            <IconButton
              type="button"
              title="Not helpful — give feedback"
              aria-label="Not helpful — give feedback"
              active={badResponse.open}
            >
              <ThumbsDown className="h-4 w-4" />
            </IconButton>
          </BadResponsePopover>
        </>
      )}
    </div>
  );
}
